// ============================================================================
// Hooks TanStack Query — COMITES (E3a) : GET /api/committees + mutations
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). La logique d'autorisation vit
// au SERVEUR (viewer renvoye par l'API — jamais recalcule au callsite, lecon
// GA-05) ; le SPA consomme viewer.* pour les affordances UI.
// Erreurs mutation -> code court ?error= (deep-linkable, parite F-402/F-410)
// mappe sur un message i18n par les pages (lecon GA-21).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiForbiddenError, ApiServerError } from '@gs/api-client';

export type CommitteeStatus = 'active' | 'paused' | 'completed';

export interface CommitteeSummary {
  id: string;
  name: string;
  description: string | null;
  status: CommitteeStatus;
  targetDate: string | null;
  createdAt: string;
  memberCount: number;
  milestonesDone: number;
  milestonesTotal: number;
  lastUpdateAt: string | null;
}

export interface CommitteesListResponse {
  committees: CommitteeSummary[];
  viewer: { role: 'admin' | 'manager' | 'director' | 'employee'; canCreate: boolean };
}

export interface CommitteeMilestone {
  id: string;
  title: string;
  done: boolean;
  /** Jamais edite a l'UI (NT-L15-01). */
  targetDate: string | null;
  sortOrder: number;
}

export interface CommitteeMember {
  email: string;
  fullName: string | null;
}

export interface CommitteeUpdate {
  id: string;
  body: string;
  postedAt: string;
  authorEmail: string;
  authorName: string | null;
}

export interface CommitteeDetailResponse {
  committee: {
    id: string;
    name: string;
    description: string | null;
    status: CommitteeStatus;
    targetDate: string | null;
    createdAt: string;
  };
  milestones: CommitteeMilestone[];
  members: CommitteeMember[];
  updates: CommitteeUpdate[];
  addableUsers: CommitteeMember[];
  viewer: {
    role: 'admin' | 'manager' | 'director' | 'employee';
    email: string;
    isMember: boolean;
    canEditCommittee: boolean;
    canPostUpdate: boolean;
    canEditMilestones: boolean;
  };
}

export interface CommitteeWritePayload {
  name: string;
  description: string | null;
  status: CommitteeStatus;
  targetDate: string | null;
}

const LIST_KEY = ['gest', 'committees'] as const;
const detailKey = (id: string) => ['gest', 'committees', id] as const;

/**
 * Code court d'erreur pour `?error=` (parite deep-link F-402/F-410) : les pages
 * le mappent sur gest:comites.errors.* — jamais de code brut a l'ecran (GA-21).
 */
export function committeeErrorCode(err: unknown): string {
  if (err instanceof ApiForbiddenError) return 'forbidden';
  if (err instanceof ApiServerError) {
    if (err.status === 404) return 'notfound';
    if (err.status === 400) return 'invalid';
  }
  return 'server';
}

export function useCommittees(showAll: boolean, enabled: boolean) {
  return useQuery({
    queryKey: [...LIST_KEY, { showAll }],
    queryFn: () => api.get<CommitteesListResponse>(`/api/committees${showAll ? '?status=all' : ''}`),
    enabled,
  });
}

export function useCommitteeDetail(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: detailKey(id ?? ''),
    queryFn: () => api.get<CommitteeDetailResponse>(`/api/committees/${id}`),
    enabled: enabled && !!id,
    // 404 (id inconnu, F-408) et 403 = etats stables — pas de retry.
    retry: false,
  });
}

function useInvalidateCommittees() {
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: LIST_KEY });
    if (id) void qc.invalidateQueries({ queryKey: detailKey(id) });
  };
}

/** F-404 : creation (admin). Retourne { id } pour le drill-in post-creation. */
export function useCreateCommittee() {
  const invalidate = useInvalidateCommittees();
  return useMutation({
    mutationFn: (payload: CommitteeWritePayload) => api.post<{ id: string }>('/api/committees', payload),
    onSuccess: () => invalidate(),
  });
}

/** F-411 : name/description/status/target_date (admin, M-400/M-401). */
export function useUpdateCommittee(id: string) {
  const invalidate = useInvalidateCommittees();
  return useMutation({
    mutationFn: (payload: CommitteeWritePayload) => api.put<{ ok: boolean }>(`/api/committees/${id}`, payload),
    onSuccess: () => invalidate(id),
  });
}

/** F-412 : soft-delete + audit committee.deleted (admin). */
export function useDeleteCommittee(id: string) {
  const invalidate = useInvalidateCommittees();
  return useMutation({
    mutationFn: () => api.delete<{ ok: boolean }>(`/api/committees/${id}`),
    onSuccess: () => invalidate(id),
  });
}

/** F-424 : ajout membre (admin, M-407). */
export function useAddMember(id: string) {
  const invalidate = useInvalidateCommittees();
  return useMutation({
    mutationFn: (memberEmail: string) => api.post<{ ok: boolean }>(`/api/committees/${id}/members`, { memberEmail }),
    onSuccess: () => invalidate(id),
  });
}

/** F-423 : retrait membre (admin, M-407). */
export function useRemoveMember(id: string) {
  const invalidate = useInvalidateCommittees();
  return useMutation({
    mutationFn: (memberEmail: string) =>
      api.delete<{ ok: boolean }>(`/api/committees/${id}/members/${encodeURIComponent(memberEmail)}`),
    onSuccess: () => invalidate(id),
  });
}

/** F-425 : « Publier une mise a jour » (management + membre, GA-02/M-403). */
export function usePostUpdate(id: string) {
  const invalidate = useInvalidateCommittees();
  return useMutation({
    mutationFn: (body: string) => api.post<{ id: string }>(`/api/committees/${id}/updates`, { body }),
    onSuccess: () => invalidate(id),
  });
}

/** F-428 : suppression d'une mise a jour (admin OU auteur, M-406). */
export function useDeleteUpdate(id: string) {
  const invalidate = useInvalidateCommittees();
  return useMutation({
    mutationFn: (updateId: string) => api.delete<{ ok: boolean }>(`/api/committees/${id}/updates/${updateId}`),
    onSuccess: () => invalidate(id),
  });
}

/**
 * F-414..F-418 / M-404 : reecrit l'array COMPLET des jalons (max 50). La gestion
 * optimiste + rollback vit dans MilestonesEditor (parite PoC) — ce hook ne
 * invalide la fiche qu'au succes.
 */
export function useSaveMilestones(id: string) {
  const invalidate = useInvalidateCommittees();
  return useMutation({
    mutationFn: (milestones: CommitteeMilestone[]) =>
      api.put<{ ok: boolean }>(`/api/committees/${id}/milestones`, { milestones }),
    onSuccess: () => invalidate(id),
  });
}
